// Layer 3 (Transformation): pure reads for escalation ladder position and clock.

import type { Case } from '@/versions/v1/schema/case';
import type { EscalationLadder, EscalationTier } from '@/versions/v1/schema/escalation-ladder';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { addMinutes, asCaseTime, clockOnProductionDay, diffMinutes, formatElapsedMinutes } from '@/versions/v1/shared/clock.derive';
import { ESCALATION_LADDERS, LADDER_BY_ID } from '@/versions/v1/shared/bunker-hill-tarp-seed';
import { roleShortLabel } from '@/versions/v1/shared/org';

export type LadderTierStatus = 'done' | 'current' | 'next' | 'pending';

export type LadderTierView = {
  tier: number;
  role: PlantRoleId;
  instruction: string;
  timeLimitMinutes: number;
  escalatesTo?: PlantRoleId;
  status: LadderTierStatus;
  overdueMinutes?: number;
};

export type LadderRead = {
  ladderId: string;
  currentTier: EscalationTier;
  tierNumber: number;
  deadline: SeriesTime;
  remainingMinutes: number;
  overdue: boolean;
  overdueMinutes: number;
  escalatesTo?: PlantRoleId;
  tiers: readonly LadderTierView[];
};

function tierEnteredAt(caseItem: Case, tier: number): SeriesTime {
  if (tier === 1) return asCaseTime(caseItem.raisedAt);
  if (tier === caseItem.ladder?.tier) return caseItem.ladder.enteredAt;
  return asCaseTime(caseItem.raisedAt);
}

function doneTierOverdueMinutes(
  caseItem: Case,
  tierDef: EscalationTier,
  currentTier: number,
): number | undefined {
  if (tierDef.tier >= currentTier) return undefined;
  const entered = tierEnteredAt(caseItem, tierDef.tier);
  let exited: SeriesTime;
  if (tierDef.tier + 1 === currentTier && caseItem.ladder) {
    exited = caseItem.ladder.enteredAt;
  } else if (tierDef.tier === 1 && caseItem.ladder) {
    exited = caseItem.ladder.enteredAt;
  } else {
    return undefined;
  }
  const allowed = tierDef.timeLimitMinutes;
  const elapsed = diffMinutes(exited, entered);
  const overdue = elapsed - allowed;
  return overdue > 0 ? Math.round(overdue) : undefined;
}

export function ladderRead(
  caseItem: Case,
  cursor: SeriesTime,
  ladders: readonly EscalationLadder[] = ESCALATION_LADDERS,
): LadderRead | null {
  if (!caseItem.ladder) return null;
  const ladder = LADDER_BY_ID.get(caseItem.ladder.ladderId) ?? ladders.find((row) => row.id === caseItem.ladder!.ladderId);
  if (!ladder) return null;
  const tierNumber = caseItem.ladder.tier;
  const currentTier = ladder.tiers.find((row) => row.tier === tierNumber);
  if (!currentTier) return null;

  const enteredAt = caseItem.ladder.enteredAt;
  const deadline = addMinutes(enteredAt, currentTier.timeLimitMinutes);
  const remainingMinutes = diffMinutes(deadline, cursor);
  const overdue = remainingMinutes <= 0;
  const overdueMinutes = overdue ? Math.round(-remainingMinutes) : 0;

  const tiers: LadderTierView[] = ladder.tiers.map((tierDef) => {
    let status: LadderTierStatus;
    if (tierDef.tier < tierNumber) status = 'done';
    else if (tierDef.tier === tierNumber) status = 'current';
    else if (tierDef.tier === tierNumber + 1) status = 'next';
    else status = 'pending';
    const overdueForDone = doneTierOverdueMinutes(caseItem, tierDef, tierNumber);
    return {
      tier: tierDef.tier,
      role: tierDef.role,
      instruction: tierDef.instruction,
      timeLimitMinutes: tierDef.timeLimitMinutes,
      escalatesTo: tierDef.escalatesTo,
      status,
      overdueMinutes: overdueForDone,
    };
  });

  return {
    ladderId: ladder.id,
    currentTier,
    tierNumber,
    deadline,
    remainingMinutes: overdue ? 0 : Math.round(remainingMinutes),
    overdue,
    overdueMinutes,
    escalatesTo: currentTier.escalatesTo,
    tiers,
  };
}

export type LadderLine = {
  text: string;
  instruction: string;
  clock: string | null;
  tone: 'ok' | 'warn' | 'bad';
};

/** The one wording for a deadline that has passed (`overdue by 10m`), shared by
 *  a ladder tier past its limit and a plain respond-by past its at. */
function overdueClock(overdueMinutes: number): string {
  return `overdue by ${formatElapsedMinutes(overdueMinutes)}`;
}

/** A case held at a plain respond-by deadline (no ladder) reads the same
 *  overdue state the ladder formatter gives an overdue tier, once the cursor
 *  is past its `at`; null while the deadline still runs. */
export function respondByOverdueClock(caseItem: Case, cursor: SeriesTime): string | null {
  if (caseItem.ladder || !caseItem.respondBy) return null;
  const at = caseItem.respondBy.at.includes('T')
    ? asCaseTime(caseItem.respondBy.at)
    : clockOnProductionDay(caseItem.respondBy.at, cursor);
  const remaining = diffMinutes(at, cursor);
  return remaining < 0 ? overdueClock(-remaining) : null;
}

function ladderRoleLabel(role: PlantRoleId): string {
  return roleShortLabel(role);
}

export function formatLadderLine(read: LadderRead): LadderLine {
  const role = ladderRoleLabel(read.currentTier.role);
  const instruction = read.currentTier.instruction;
  const instructionText = `Tier ${read.tierNumber} · ${role} · ${instruction}`;

  if (read.overdue) {
    const clock = overdueClock(read.overdueMinutes);
    return {
      text: `${instructionText} · ${clock}`,
      instruction: instructionText,
      clock,
      tone: 'bad',
    };
  }

  if (!read.escalatesTo) {
    return {
      text: instructionText,
      instruction: instructionText,
      clock: null,
      tone: 'ok',
    };
  }

  const escalateRole = ladderRoleLabel(read.escalatesTo);
  const clock = `escalates to ${escalateRole} in ${formatElapsedMinutes(read.remainingMinutes)}`;
  const tone: LadderLine['tone'] = read.remainingMinutes <= 30 ? 'warn' : 'ok';
  return {
    text: `${instructionText} · ${clock}`,
    instruction: instructionText,
    clock,
    tone,
  };
}

export function ladderLineForCase(caseItem: Case, cursor: SeriesTime): LadderLine | null {
  const read = ladderRead(caseItem, cursor);
  if (!read) return null;
  return formatLadderLine(read);
}
